import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { InputHelperTextComponent } from 'app/shared/components/input-helper-text/input-helper-text.component';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { HelperService } from 'app/shared/services/helper.service';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';

@Component({
  selector: 'app-configuration-select-form-field',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, NgSelectModule, InputHelperTextComponent],
  templateUrl: './configuration-select-form-field.component.html',
  styleUrl: './configuration-select-form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ConfigurationSelectFormFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ConfigurationSelectFormFieldComponent),
      multi: true,
    }
  ],
})
export class ConfigurationSelectFormFieldComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() label: string = '';
  @Input() type: string = 'text';
  @Input() placeholder: string = 'placeholders.please_select';
  @Input() isRequired: boolean = false;
  @Input() infoText: string = '';
  @Input() clearable: boolean = false;
  @Input() searchable: boolean = false;
  @Input() bindLabel: string = 'value';
  @Input() bindValue: string = 'value';
  @Input() items: any = [];
  @Input() multipleSelect: boolean = false;
  @Input() users: boolean = false;
  @Input() viewMode!: boolean;
  @Input() fullWidth!: boolean;
  @Input() customClass: string = '';
  @Input() externalSearch: boolean = false;
  @Input() isLabelTopPosition: boolean = false;
  @Input() helpText!: string;
  @Input() takeCount: number = 50;
  skipCount = 0;
  hasMoreItems = true;
  isLoadingMore = false;

  @Input() params: any = {
    type: null,
    entry1: null,
    entry2: null,
    entry3: null,
    entry4: null,
    entry5: null,
    entry6: null,
  };

  @Input() entry = '';
  @Input() callApiOnOpen: boolean = true;

  @Output() onSelectEvent = new EventEmitter<void>();
  @Output() onSearchEvent = new EventEmitter<string>();
  @Output() onClearEvent = new EventEmitter<boolean>();
  @Output() onOpenEvent = new EventEmitter<void>();

  formControl: FormControl = new FormControl<string>('');
  errorText: string = '';
  inlineEditMode!: boolean;
  firstOpenSelect: boolean = true;

  searchInput$ = new Subject<string>();

  private unsubscribe$ = new Subject<void>()

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private helperService: HelperService,
    private configurationService: ConfigurationService
  ) {
    this.searchInput$
      .pipe(
        debounceTime(300),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((data: string) => {
        this.params[this.entry] = data;
        if ((data.length === 0 || data.length > 2) && this.externalSearch) {
          this.skipCount = 0;
          this.hasMoreItems = true;
          this.items = [];
          this.onSearchEvent.emit(data);
          this.getData();
        }
      });
  }

  onTouch: () => void = () => { };
  onChange: (value: any) => void = () => { };

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.formControl.disable() : this.formControl.enable();
  }

  writeValue(value: string): void {
    this.formControl.setValue(value, { emitEvent: false });

    this.changeDetectorRef.markForCheck();
  }

  ngOnInit(): void {
    this.formControl.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((value) => {
        this.onChange(value);
      });
  }

  returnTrue(): boolean {
    return true;
  }

  validate(control: FormControl) {
    setTimeout(() => {
      this.errorText = this.helperService.handleFieldsErrors(control);
      this.changeDetectorRef.markForCheck();
    });
    return null;
  }

  onSearch($event: any) {
    const term = $event.term;
    this.searchInput$.next(term);
  }

  change(data: any) {
    this.onSelectEvent.emit(data);
  }

  onClear() {
    this.params[this.entry] = '';
    this.getData();
    this.onClearEvent.emit(true);
  }

  onOpen() {
    this.onOpenEvent.emit();
    this.params[this.entry] = '';
    this.skipCount = 0;
    if (this.callApiOnOpen) {
      this.getData();
      return;
    }

    if (this.firstOpenSelect) {
      const selectedValue = this.params[this.entry];
      this.params[this.entry] = '';
      this.getData(selectedValue);
      this.firstOpenSelect = false;
    }

  }

  getData(selectedValue?: string, append = false) {

    this.configurationService.getDropdownConfigurations(this.params, this.entry, this.skipCount, this.takeCount)
      .pipe(take(1))
      .subscribe((res: any) => {
        this.hasMoreItems = res.length === this.takeCount;

        if (append) {
          this.items = [...this.items, ...res];
        } else if (!!selectedValue) {
          this.handleSelectedValue(selectedValue, res);
        } else {
          this.items = res;
        }

        this.changeDetectorRef.markForCheck();
      });
  }

  handleSelectedValue(selectedValue: string, res: any) {
    for (let item of res) {
      if (item.value === selectedValue) {
        this.items = res;
        return;
      }
    }

    this.checkIfItemExist(selectedValue, res);
  }

  checkIfItemExist(selectedValue: string, res: any) {
    this.params[this.entry] = selectedValue;
    this.configurationService.getDropdownConfigurations(this.params, this.entry)
      .pipe(take(1))
      .subscribe((resSelectedValue: any) => {

        if (resSelectedValue && resSelectedValue.length > 0) {

          for (let element of resSelectedValue) {
            if (element.value === selectedValue) {
              this.items = [{ value: selectedValue }, ...res];
              this.changeDetectorRef.markForCheck();
              return;
            }
          }
        }

        this.items = res;
        this.changeDetectorRef.markForCheck();
      });

  }

  onScrollToEnd() {
    if (this.hasMoreItems) {
      this.skipCount += this.takeCount;
      this.getData(undefined, true);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
